import React, { useEffect } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import OpenInNew from '@material-ui/icons/OpenInNew';
import Refresh from '@material-ui/icons/Refresh';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';

/**
 * Jenkins tab for apps created by DevOps/app-bootstrap.
 *
 * Each such app has three jobs (BackendDeployment, ConfigUpdate,
 * SecretUpdate), plus FrontendDeployment for fullstack apps, in
 * `<OrgFolder>/Services/<app>/` on the staging Jenkins
 * (flyingdutchman) and/or the production Jenkins (blackpearl). The community
 * Jenkins plugin can only show one Jenkins instance per entity, so this tab
 * reads both through the internal backend (GET /jenkins/app-pipelines) and
 * shows one card per environment. Read-only: no rerun or trigger buttons.
 *
 * Opt-in annotations (written by app-bootstrap into catalog-info.yaml):
 *   sparrow.io/jenkins-folder: Platforms/Services/test05
 *   sparrow.io/environments:   staging,production
 */

export const JENKINS_FOLDER_ANNOTATION = 'sparrow.io/jenkins-folder';
export const ENVIRONMENTS_ANNOTATION = 'sparrow.io/environments';

export const isAppPipelinesEntity = (entity: {
  metadata?: { annotations?: Record<string, string> };
}) => Boolean(entity.metadata?.annotations?.[JENKINS_FOLDER_ANNOTATION]);

type Build = {
  number: number;
  result: string | null;
  building: boolean;
  timestamp: number;
  duration: number;
  url: string;
};
type Job = {
  name: string;
  exists: boolean;
  url: string | null;
  lastBuild: Build | null;
  recentBuilds: Build[];
};
type EnvironmentPipelines = {
  env: string;
  instance: string;
  jenkinsHost: string | null;
  folderUrl: string | null;
  jobs: Job[];
  error?: string;
};

const ENV_LABEL: Record<string, string> = { staging: 'Staging', production: 'Production' };
const JOB_PURPOSE: Record<string, string> = {
  BackendDeployment: 'Build the image and deploy',
  FrontendDeployment: 'Build the frontend and publish it to the CDN',
  ConfigUpdate: 'Sync config and restart',
  SecretUpdate: 'Update Vault secrets (with approval)',
};

const useStyles = makeStyles(theme => ({
  jobName: { fontWeight: 500 },
  purpose: { color: theme.palette.text.secondary, fontSize: '0.8rem' },
  muted: { color: theme.palette.text.secondary },
  dots: { display: 'flex', gap: 4, alignItems: 'center' },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    display: 'inline-block',
  },
  envChip: { fontWeight: 600, marginRight: theme.spacing(1) },
  tableWrap: { overflowX: 'auto' },
  error: { padding: theme.spacing(2), color: theme.palette.text.secondary },
}));

const STATUS: Record<string, { label: string; color: string }> = {
  SUCCESS: { label: 'Success', color: '#2e7d32' },
  FAILURE: { label: 'Failed', color: '#c62828' },
  UNSTABLE: { label: 'Unstable', color: '#ef8f00' },
  ABORTED: { label: 'Aborted', color: '#757575' },
  NOT_BUILT: { label: 'Not built', color: '#9e9e9e' },
  RUNNING: { label: 'Running', color: '#1565c0' },
};
const statusOf = (b: Build) => (b.building ? STATUS.RUNNING : STATUS[b.result ?? ''] ?? STATUS.NOT_BUILT);

function relativeTime(ts: number): string {
  if (!ts) return '';
  const minutes = Math.round((Date.now() - ts) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function duration(ms: number, building: boolean): string {
  if (building) return 'running';
  if (!ms) return '';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

const JobRow = ({ job }: { job: Job }) => {
  const classes = useStyles();
  const last = job.lastBuild;
  let emptyText: string | undefined;
  if (!job.exists) emptyText = 'Not created on this Jenkins yet';
  else if (!last) emptyText = 'No runs yet';
  return (
    <TableRow>
      <TableCell>
        <div className={classes.jobName}>{job.name}</div>
        <div className={classes.purpose}>{JOB_PURPOSE[job.name]}</div>
      </TableCell>
      {emptyText || !last ? (
        <TableCell colSpan={5} className={classes.muted}>
          {emptyText}
        </TableCell>
      ) : (
        <>
          <TableCell>
            <Link href={last.url} target="_blank" rel="noopener">
              #{last.number}
            </Link>
          </TableCell>
          <TableCell>
            <Chip
              size="small"
              label={statusOf(last).label}
              style={{ backgroundColor: statusOf(last).color, color: '#fff' }}
            />
          </TableCell>
          <TableCell>
            <Tooltip title={new Date(last.timestamp).toLocaleString()}>
              <span>{relativeTime(last.timestamp)}</span>
            </Tooltip>
          </TableCell>
          <TableCell>{duration(last.duration, last.building)}</TableCell>
          <TableCell>
            <div className={classes.dots}>
              {job.recentBuilds.map(b => (
                <Tooltip key={b.number} title={`#${b.number} · ${statusOf(b).label} · ${relativeTime(b.timestamp)}`}>
                  <a href={b.url} target="_blank" rel="noopener" aria-label={`Build ${b.number}: ${statusOf(b).label}`}>
                    <span className={classes.dot} style={{ backgroundColor: statusOf(b).color }} />
                  </a>
                </Tooltip>
              ))}
            </div>
          </TableCell>
        </>
      )}
      <TableCell align="right">
        {job.url && (
          <Tooltip title="Open in Jenkins">
            <IconButton size="small" href={job.url} target="_blank" rel="noopener" aria-label={`Open ${job.name} in Jenkins`}>
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};

const EnvironmentCard = ({ env, folder }: { env: EnvironmentPipelines; folder: string }) => {
  const classes = useStyles();
  const title = (
    <Box display="flex" alignItems="center" flexWrap="wrap">
      <Chip
        size="small"
        className={classes.envChip}
        label={ENV_LABEL[env.env] ?? env.env}
        style={{
          backgroundColor: env.env === 'production' ? '#ede7f6' : '#e3f2fd',
          color: env.env === 'production' ? '#4527a0' : '#0d47a1',
        }}
      />
      <span>{env.jenkinsHost ?? 'Jenkins'}</span>
    </Box>
  );
  return (
    <InfoCard
      title={title}
      subheader={folder}
      action={
        env.folderUrl ? (
          <Button size="small" href={env.folderUrl} target="_blank" rel="noopener" endIcon={<OpenInNew fontSize="small" />}>
            Open folder
          </Button>
        ) : undefined
      }
      noPadding
    >
      {env.error ? (
        <Typography className={classes.error}>{env.error}</Typography>
      ) : (
        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pipeline</TableCell>
                <TableCell>Last build</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Last 5 runs</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {env.jobs.map(job => (
                <JobRow key={job.name} job={job} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </InfoCard>
  );
};

export const EntityAppPipelinesContent = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const annotations = entity.metadata.annotations ?? {};
  const folder = annotations[JENKINS_FOLDER_ANNOTATION] ?? '';
  const envs = annotations[ENVIRONMENTS_ANNOTATION] ?? 'staging,production';

  const { value, loading, error, retry } = useAsyncRetry(async () => {
    const base = await discoveryApi.getBaseUrl('internal');
    const qs = new URLSearchParams({ folder, envs });
    const res = await fetchApi.fetch(`${base}/jenkins/app-pipelines?${qs}`);
    if (!res.ok) throw new Error(`Launchpad returned HTTP ${res.status}`);
    return (await res.json()) as { folder: string; environments: EnvironmentPipelines[] };
  }, [folder, envs]);

  // Refresh while something is running, so a deploy can be followed from here.
  const running = value?.environments.some(e => e.jobs.some(j => j.lastBuild?.building)) ?? false;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(retry, 15_000);
    return () => clearInterval(t);
  }, [running, retry]);

  if (loading && !value) return <Progress />;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="textSecondary">
            Deploy, config and secret pipelines for each environment. Runs start in Jenkins.
            {running ? ' Refreshing every 15 s while a build runs.' : ''}
          </Typography>
          <Button size="small" startIcon={<Refresh />} onClick={retry} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Grid>
      {error && (
        <Grid item xs={12}>
          <Typography color="error">Could not load pipelines: {error.message}</Typography>
        </Grid>
      )}
      {value?.environments.map(env => (
        <Grid item xs={12} key={env.env}>
          <EnvironmentCard env={env} folder={folder} />
        </Grid>
      ))}
    </Grid>
  );
};

export default EntityAppPipelinesContent;
